import React, { useEffect, useState, useMemo, useContext, useCallback } from "react";
import { Calendar, Filter, Grid, List, ChevronLeft, ChevronRight, Search, RefreshCw, Users, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import { toast } from "react-toastify";
import { AuthContext } from "../../../context/AuthContext";
import AppointmentCard from "../AppointmentCard";
import AppointmentTableRow from "../AppointmentTableRow";

const AllAppointment = () => {
  const {
    appointments,
    appointmentsLoading,
    appointmentsError,
    getUpcomingAppointments,
    setAppointments,
    setAppointmentsError,
  } = useContext(AuthContext);

  // State management
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [loadingUpdates, setLoadingUpdates] = useState({});
  
  // Filter and view states
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOption, setSortOption] = useState("date");
  const [viewType, setViewType] = useState("card");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Load appointments on mount
  useEffect(() => {
    if (!appointments.length) {
      getUpcomingAppointments();
    }
  }, [appointments, getUpcomingAppointments]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFilter, statusFilter, sortOption, searchTerm]);

  // Handle input changes for updates
  const handleInputChange = useCallback((id, field, value) => {
    setPendingUpdates((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  }, []);

  // Handle appointment update
  const handleUpdate = useCallback(async (id) => {
    const updates = pendingUpdates[id];
    if (!updates) {
      toast.error("No changes to update.");
      return;
    }

    setLoadingUpdates((prev) => ({ ...prev, [id]: true }));

    try {
      await API.put(`/api/receptionist/appointment/${id}`, updates);

      setAppointments((prev) =>
        prev.map((appointment) =>
          appointment._id === id ? { ...appointment, ...updates } : appointment
        )
      );

      setPendingUpdates((prev) => {
        const { [id]: _, ...rest } = prev;
        return rest;
      });
      
      toast.success("Appointment updated successfully!");
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update appointment";
      setAppointmentsError(message);
      toast.error(message);
    } finally {
      setLoadingUpdates((prev) => ({ ...prev, [id]: false }));
    }
  }, [pendingUpdates, setAppointments, setAppointmentsError]);

  // Handle appointment deletion
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) return;

    try {
      await API.delete(`/api/receptionist/appointment/${id}`);
      setAppointments((prev) => prev.filter((appointment) => appointment._id !== id));
      toast.success("Appointment deleted successfully!");
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete appointment";
      setAppointmentsError(message);
      toast.error(message);
    }
  }, [setAppointments, setAppointmentsError]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    getUpcomingAppointments();
    toast.info("Appointments refreshed!");
  }, [getUpcomingAppointments]);

  // Filter and sort appointments
  const filteredAppointments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let filtered = appointments.filter((appointment) => {
      // Search filtering
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          appointment.name?.toLowerCase().includes(searchLower) ||
          appointment.reason?.toLowerCase().includes(searchLower) ||
          appointment.status?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Date filtering
      if (dateFilter !== "all") {
        const appointmentDate = new Date(appointment.date);
        appointmentDate.setHours(0, 0, 0, 0);

        switch (dateFilter) {
          case "today":
            if (appointmentDate.getTime() !== today.getTime()) return false;
            break;
          case "upcoming":
            if (appointmentDate.getTime() <= today.getTime()) return false;
            break;
          case "past":
            if (appointmentDate.getTime() >= today.getTime()) return false;
            break;
        }
      }

      // Status filtering
      if (statusFilter !== "all") {
        if (appointment.status.toLowerCase() !== statusFilter.toLowerCase()) return false;
      }

      return true;
    });

    // Sorting
    if (sortOption !== "none") {
      filtered.sort((a, b) => {
        switch (sortOption) {
          case "name":
            const nameA = a.name?.toLowerCase() || "";
            const nameB = b.name?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
          case "date":
            return new Date(a.date) - new Date(b.date);
          case "status":
            return a.status.localeCompare(b.status);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [appointments, dateFilter, statusFilter, sortOption, searchTerm]);

  // Statistics calculations
  const statistics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return {
      total: appointments.length,
      today: appointments.filter(apt => {
        const aptDate = new Date(apt.date);
        aptDate.setHours(0, 0, 0, 0);
        return aptDate.getTime() === today.getTime();
      }).length,
      completed: appointments.filter(apt => apt.status.toLowerCase() === 'completed').length,
      pending: appointments.filter(apt => apt.status.toLowerCase() === 'pending').length,
    };
  }, [appointments]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const paginatedAppointments = filteredAppointments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setDateFilter("all");
    setStatusFilter("all");
    setSortOption("date");
    setCurrentPage(1);
  }, []);

  // Render table view
  const renderTableView = () => (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              {["Patient Name", "Appointment Date", "Time Slot", "Treatment", "Status", "Notes", "Actions"].map((header) => (
                <th
                  key={header}
                  className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {paginatedAppointments.map((item) => (
              <AppointmentTableRow
                key={item._id}
                item={item}
                handleInputChange={handleInputChange}
                handleUpdate={handleUpdate}
                handleDelete={handleDelete}
                loadingUpdates={loadingUpdates}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (appointmentsLoading && appointments.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
      <div className=" mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="text-center bg-white rounded-2xl shadow-sm p-6 border">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Appointment Management
          </h1>
          <p className="text-gray-600 text-lg">Manage and track all patient appointments efficiently</p>
        </div>

        {/* Statistics Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Appointments</p>
                <p className="text-3xl font-bold">{statistics.total}</p>
              </div>
              <Users className="w-8 h-8 text-blue-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Today's Appointments</p>
                <p className="text-3xl font-bold">{statistics.today}</p>
              </div>
              <Clock className="w-8 h-8 text-emerald-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Completed</p>
                <p className="text-3xl font-bold">{statistics.completed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-200" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">Pending</p>
                <p className="text-3xl font-bold">{statistics.pending}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-200" />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {appointmentsError && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center justify-between">
            <p className="text-red-700 font-medium">{appointmentsError}</p>
            <button
              onClick={handleRefresh}
              className="text-red-600 hover:text-red-800 font-medium text-sm px-4 py-2 rounded-lg hover:bg-red-100 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Search and Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={20} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, reason, or status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                className="p-3 text-gray-500 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                title="Refresh appointments"
              >
                <RefreshCw size={20} />
              </button>
              
              <div className="text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-xl">
                <span className="font-semibold text-gray-900">{filteredAppointments.length}</span> 
                <span className="ml-1">appointments found</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Date Filter */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                <Calendar size={16} />
                Date Filter
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                <Filter size={16} />
                Status Filter
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="all">All Status</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            {/* Sort Option */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-3 block">Sort By</label>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="date">Date</option>
                <option value="name">Patient Name</option>
                <option value="status">Status</option>
                <option value="none">No Sorting</option>
              </select>
            </div>

            {/* View Type & Actions */}
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-3 block">View & Actions</label>
              <div className="flex gap-2">
                <div className="flex border border-gray-300 rounded-xl overflow-hidden flex-1">
                  <button
                    onClick={() => setViewType("card")}
                    className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                      viewType === "card"
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Grid size={16} className="inline mr-2" />
                    Cards
                  </button>
                  <button
                    onClick={() => setViewType("table")}
                    className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
                      viewType === "table"
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    } border-l border-gray-300`}
                  >
                    <List size={16} className="inline mr-2" />
                    Table
                  </button>
                </div>
                
                <button
                  onClick={clearFilters}
                  className="px-4 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-6 border-t border-gray-200">
            <span className="text-sm text-gray-600">
              Showing <span className="font-semibold">{paginatedAppointments.length}</span> of{" "}
              <span className="font-semibold">{filteredAppointments.length}</span> appointments
            </span>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="6">6</option>
                <option value="12">12</option>
                <option value="18">18</option>
                <option value="24">24</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          </div>
        </div>

        {/* Content */}
        {filteredAppointments.length > 0 ? (
          <>
            {/* Appointments Display */}
            {viewType === "card" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedAppointments.map((item) => (
                  <AppointmentCard
                    key={item._id}
                    item={item}
                    handleInputChange={handleInputChange}
                    handleUpdate={handleUpdate}
                    handleDelete={handleDelete}
                    loadingUpdates={loadingUpdates}
                  />
                ))}
              </div>
            ) : (
              renderTableView()
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white rounded-2xl shadow-sm border p-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-600">
                    Page <span className="font-semibold">{currentPage}</span> of{" "}
                    <span className="font-semibold">{totalPages}</span> ({filteredAppointments.length} total)
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 text-gray-500 border rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-4 py-2 text-sm rounded-xl transition-colors ${
                            currentPage === pageNum
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 border hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 text-gray-500 border rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border">
            <div className="text-gray-400 mb-6">
              <Calendar size={64} className="mx-auto" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-700 mb-3">
              {searchTerm || dateFilter !== "all" || statusFilter !== "all" 
                ? "No appointments match your criteria" 
                : "No appointments found"
              }
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {searchTerm || dateFilter !== "all" || statusFilter !== "all"
                ? "Try adjusting your search or filters to see more results"
                : "Appointments will appear here once they are created"
              }
            </p>
            {(searchTerm || dateFilter !== "all" || statusFilter !== "all") && (
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllAppointment;